import { Plus, X } from "lucide-react";
import { useMemo, useState } from "react";

import { EmptyState } from "@/components/common/EmptyState";
import { FloatingButton } from "@/components/common/FloatingButton";
import { SearchInput } from "@/components/common/SearchInput";
import { StatCard } from "@/components/common/StatCard";
import { StockItemList, type StockRow } from "@/components/common/StockItemList";
import { BackButton } from "@/components/ui/back-button";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { useCurrency } from "@/features/accounts/hooks/useCurrency";
import SupplyingPage from "@/features/product-stocks/SupplyingPage";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import { stockStatus } from "@/lib/expiry";
import { currencySymbol, formatAmount, plural } from "@/lib/format";
import type { ProductStockStateDto } from "@/types/stock.types";

export default function ProductStockDetailPage({
  stock,
  onBack,
  onRefresh,
}: {
  stock: ProductStockStateDto;
  onBack: () => void;
  onRefresh: () => void;
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [isSupplying, setIsSupplying] = useState(false);
  const { currency } = useCurrency();
  const isDesktop = useIsDesktop();

  const rows: StockRow[] = useMemo(
    () =>
      stock.items.map((item) => ({
        id: item.product.id,
        name: item.product.name,
        measurementUnit: item.product.measurementUnit,
        quantity: item.quantity,
        expiryDate: item.expiryDate,
        unitPrice: item.product.price,
      })),
    [stock.items],
  );

  const toWatch = useMemo(
    () =>
      rows.filter((row) => {
        const status = stockStatus(row.quantity, row.expiryDate);
        return status === "expiring" || status === "expired" || status === "out";
      }).length,
    [rows],
  );

  const visible = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return term ? rows.filter((row) => row.name.toLowerCase().includes(term)) : rows;
  }, [rows, searchTerm]);

  if (isSupplying) {
    return (
      <SupplyingPage
        initialProductStockId={stock.id}
        onBack={() => setIsSupplying(false)}
        onDone={async () => {
          await onRefresh();
          setIsSupplying(false);
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-start gap-3">
        <BackButton onClick={onBack} />
        <PageHeader
          className="flex-1"
          title={stock.name}
          subtitle="Ce que vous pouvez vendre aujourd’hui"
          action={
            isDesktop ? (
              <Button size="lg" onClick={() => setIsSupplying(true)}>
                <Plus />
                Ravitailler
              </Button>
            ) : null
          }
        />
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        <StatCard
          label="Valeur du stock"
          value={formatAmount(stock.monetaryValue, currency)}
          unit={currencySymbol(currency)}
        />
        <StatCard label="Produits en stock" value={String(rows.length)} />
        <StatCard
          label="À surveiller"
          value={String(toWatch)}
          unit={plural(toWatch, "produit")}
          tone={toWatch > 0 ? "warning" : "default"}
        />
      </div>

      <SearchInput
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Rechercher un produit…"
      />

      {visible.length === 0 ? (
        <EmptyState
          title="Rien à afficher"
          hint={
            searchTerm
              ? "Aucun produit ne correspond à votre recherche."
              : "Ravitailler ce stock depuis vos matières premières."
          }
          action={
            searchTerm ? (
              <Button type="button" variant="outline" onClick={() => setSearchTerm("")}>
                <X />
                Effacer la recherche
              </Button>
            ) : (
              <Button type="button" onClick={() => setIsSupplying(true)}>
                <Plus />
                Ravitailler
              </Button>
            )
          }
        />
      ) : (
        <StockItemList rows={visible} currency={currency} quantityLabel="Reste" />
      )}

      {!isDesktop && <FloatingButton icon={Plus} ariaLabel="Ravitailler" onClick={() => setIsSupplying(true)} />}
    </div>
  );
}
